import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Toolbar, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useGridApiContext, useGridRootProps } from "@mui/x-data-grid";

// Heuristic to detect date-like columns and ensure proper sorting
const isDateLikeField = (field = "", type) => {
	if (type === "date" || type === "dateTime") return true;
	const f = String(field).toLowerCase();
	return f.includes("date") || f.endsWith("_at") || f.endsWith("_on");
};

const dateSortComparator = (v1, v2) => {
	const t1 = v1 ? new Date(v1).getTime() : 0;
	const t2 = v2 ? new Date(v2).getTime() : 0;
	return t1 - t2;
};

const Datagrid = ({
	data,
	columns,
	onSelect,
	customButtons,
	onPageChange,
	rowIdField,
	sortBy,
	pageSize = 10,
	showSearchBar = true,
	checkboxSelection = false,
	pageScopedHeaderSelection = true,
	rowClick = false,
	onRowClick,
	pageSizeOptions,
	disableSelectAll = false,
	rowSelectionModel,
	keepNonExistentSelected = false,
	autoHeight = true,
	gridHeight = 520,
	handleSearch,
	onColumnVisibilityModelChange,
	columnVisibilityModel: externalColumnVisibilityModel,
	localeText,
	density = "standard",
	loading = false,
	// NEW PROPS FOR SERVER-SIDE PAGINATION:
	paginationMode = "client",          // "client" or "server"
	rowCount,                           // Total rows (required for server mode)
	paginationModel,                    // { page: 0, pageSize: 50 }
	onPaginationModelChange,
	filterMode = "client",
	filterModel,
	onFilterModelChange,
	lockActiveColumnInManageColumns,
	preventHidingLastColumn,
	slots,
	slotProps,
	gridSx,
	scrollToRowId,
	getRowHeight,

}) => {
	const [searchText, setSearchText] = useState("");
	const [filteredData, setFilteredData] = useState(data);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [activeMenuColumnField, setActiveMenuColumnField] = useState(null);
	const [clientPaginationModel, setClientPaginationModel] = useState(() => ({
		page: 0,
		pageSize: Number.isFinite(Number(pageSize)) ? Number(pageSize) : 10,
	}));
	const suppressNextSelectionEventRef = useRef(false);
	const gridWrapperRef = useRef(null);
	const { t } = useTranslation();
	const normalizedPageSize = Number.isFinite(Number(pageSize)) ? Number(pageSize) : 10;
	const normalizedPageSizeOptions = useMemo(() => {
		const fallback = [5, 10, 25, 50];
		const base = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 ? pageSizeOptions : fallback;
		const options = new Set(base.map((val) => Number(val)).filter((val) => Number.isFinite(val) && val > 0));
		options.add(normalizedPageSize);
		return Array.from(options).sort((a, b) => a - b);
	}, [pageSizeOptions, normalizedPageSize]);

	const enhancedColumns = useMemo(() => {
		return (columns || []).map((col) => {
			if (!col || col.sortComparator) return col;
			if (isDateLikeField(col.field, col.type)) {
				return { ...col, sortComparator: dateSortComparator };
			}
			return col;
		});
	}, [columns]);

	const shallowEqualModels = (a = {}, b = {}) => {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (let i = 0; i < aKeys.length; i += 1) {
			const key = aKeys[i];
			if (a[key] !== b[key]) return false;
		}
		return true;
	};

	const enforceColumnVisibilityRules = React.useCallback(
		(model = {}) => {
			const next = { ...model };
			const fields = new Set((enhancedColumns || []).map((col) => col?.field).filter(Boolean));

			// Remove stale fields from the visibility model.
			Object.keys(next).forEach((field) => {
				if (!fields.has(field)) delete next[field];
			});

			if (preventHidingLastColumn) {
				const visibleFields = (enhancedColumns || [])
					.filter((col) => col?.field && next[col.field] !== false)
					.map((col) => col.field);

				if (visibleFields.length === 0) {
					const fallbackField =
						(lockActiveColumnInManageColumns && activeMenuColumnField && fields.has(activeMenuColumnField))
							? activeMenuColumnField
							: (enhancedColumns || []).find((col) => col?.field)?.field;
					if (fallbackField) delete next[fallbackField];
				}
			}

			return next;
		},
		[enhancedColumns, lockActiveColumnInManageColumns, activeMenuColumnField, preventHidingLastColumn]
	);

	useEffect(() => {
		setColumnVisibilityModel((prev) => {
			const normalized = enforceColumnVisibilityRules(prev);
			return shallowEqualModels(prev, normalized) ? prev : normalized;
		});
	}, [enforceColumnVisibilityRules]);

	useEffect(() => {
		if (!externalColumnVisibilityModel) return;
		const normalized = enforceColumnVisibilityRules(externalColumnVisibilityModel);
		setColumnVisibilityModel((prev) =>
			shallowEqualModels(prev, normalized) ? prev : normalized
		);
	}, [externalColumnVisibilityModel, enforceColumnVisibilityRules]);

	const handleColumnVisibilityModelChange = useCallback(
		(nextModel) => {
			const normalized = enforceColumnVisibilityRules(nextModel || {});
			setColumnVisibilityModel(normalized);
			if (typeof onColumnVisibilityModelChange === "function") {
				onColumnVisibilityModelChange(normalized);
			}
		},
		[enforceColumnVisibilityRules, onColumnVisibilityModelChange]
	);

	const stripHtml = (html = "") =>
		String(html)
			.replace(/<style[\s\S]*?<\/style>/gi, " ")
			.replace(/<script[\s\S]*?<\/script>/gi, " ")
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/g, " ")
			.replace(/\s+/g, " ")
			.trim();

	const extractTextFromReactNode = (node) => {
		if (node == null || typeof node === "boolean") return "";
		if (typeof node === "string" || typeof node === "number") return String(node);
		if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(" ");

		if (typeof node === "object" && node.props) {
			const html = node.props?.dangerouslySetInnerHTML?.__html;
			if (html) return stripHtml(html);

			const candidates = [
				node.props.label,
				node.props.text,
				node.props.name,
				node.props.title,
				node.props.value,
			]
				.map(extractTextFromReactNode)
				.filter(Boolean);

			if (candidates.length) return candidates.join(" ");
			return extractTextFromReactNode(node.props.children);
		}

		return "";
	};

	const callValueFormatterSafely = (col, params, value) => {
		if (typeof col.valueFormatter !== "function") return null;

		try {
			const out1 = col.valueFormatter({ ...params, value });
			if (out1 != null && out1 !== "") return String(out1);
		} catch { }
		try {
			const out2 = col.valueFormatter(value);
			if (out2 != null && out2 !== "") return String(out2);
		} catch { }

		return null;
	};

	const getDisplayedCellText = ({ col, row, getRowId }) => {
		if (!col?.field) return ""; // skip "Actions" etc.

		const field = col.field;
		const rawValue = row?.[field];

		const params = {
			id: getRowId?.(row) ?? row?.id ?? row?.[field],
			field,
			row,
			value: rawValue,
		};

		// optional explicit hook (best for complex cases)
		if (typeof col.getSearchValue === "function") {
			try {
				return String(col.getSearchValue(params) ?? "");
			} catch {
				return "";
			}
		}

		// try renderCell "display text"
		if (typeof col.renderCell === "function") {
			try {
				const rendered = col.renderCell(params);
				const text = extractTextFromReactNode(rendered);
				if (text) return text;
			} catch {
				// ignore
			}
		}

		// valueGetter fallback
		let value = rawValue;
		if (typeof col.valueGetter === "function") {
			try {
				value = col.valueGetter(params);
			} catch {
				value = rawValue;
			}
		}

		// valueFormatter fallback
		const formatted = callValueFormatterSafely(col, params, value);
		if (formatted != null) return formatted;

		return value == null ? "" : String(value);
	};

	const defaultFilter = (query, rows, cols, getRowId) => {
		const q = (query || "").toLowerCase().trim();
		if (!q) return rows;

		return (rows || []).filter((row) =>
			(cols || []).some((col) => {
				if (!col?.field) return false;              // skip columns without field
				if (col.searchable === false) return false; // allow opt-out
				const text = getDisplayedCellText({ col, row, getRowId }).toLowerCase();
				return text.includes(q);
			})
		);
	};



	const runSearch = (query, rows) => {
		const q = (query || "").toLowerCase();

		//   Use custom handler if provided
		if (typeof handleSearch === "function") {
			try {
				const result = handleSearch({ query: q, data: rows, columns: enhancedColumns });

				// If handler returns an array, we use it
				if (Array.isArray(result)) {
					setFilteredData(result);
					return;
				}

				// If handler returns a Promise, resolve it
				if (result && typeof result.then === "function") {
					result
						.then((nextRows) => {
							if (Array.isArray(nextRows)) setFilteredData(nextRows);
						})
						.catch(() => {
							// If custom search errors, fall back to empty (or you can fall back to defaultFilter)
							setFilteredData([]);
						});
					return;
				}

				// If it returns void/anything else, assume the parent is handling side effects.
				return;
			} catch {
				setFilteredData([]);
				return;
			}
		}

		const filteredRows = defaultFilter(
			query,
			data,
			columns,
			(r) => r?.[rowIdField] ?? r?.id ?? r?.document_id
		);

		setFilteredData(filteredRows);
	};

	// Keep filteredData in sync with data changes + current search query
	useEffect(() => {
		runSearch(searchText, data || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, searchText, enhancedColumns, handleSearch]);

	// FIXED: Guard against rows with missing or null IDs - improved version
	const safeRows = useMemo(() => {
		

		const validRows = [];
		const invalidRows = [];

		(filteredData || []).forEach((row, index) => {
			// Ensure row is an object and not null
			if (!row || typeof row !== 'object') {
				invalidRows.push({ index, reason: 'not an object', row });
				return;
			}

			// Check for any possible ID field
			const id = row[rowIdField] ?? row.id ?? row.document_id ?? row.uuid;

			if (id === null || id === undefined || id === '') {
				invalidRows.push({ index, reason: 'missing ID', row });
				return;
			}

			validRows.push(row);
		});

		if (invalidRows.length > 0) {
			console.warn('DATAGRID: Filtered out rows with invalid IDs:', invalidRows);
			console.warn(`DATAGRID: Valid rows: ${validRows.length}, Invalid rows: ${invalidRows.length}`);
		}

		return validRows;
	}, [filteredData, rowIdField]);

	const normalizeSelectionIds = (selection) => {
		if (Array.isArray(selection)) return selection;
		if (selection && typeof selection === "object") {
			if (selection.ids instanceof Set) return Array.from(selection.ids);
			if (selection instanceof Set) return Array.from(selection);
		}
		return [];
	};

	const getCurrentPageIds = () => {
		const page = Number(clientPaginationModel?.page) || 0;
		const size = Number(clientPaginationModel?.pageSize) || normalizedPageSize;
		const start = page * size;
		const end = start + size;
		return safeRows.slice(start, end).map((row) => getRowIdSafe(row));
	};

	const handleSelectionChange = (newSelection) => {
		if (!onSelect) return;
		if (suppressNextSelectionEventRef.current) {
			suppressNextSelectionEventRef.current = false;
			return;
		}
		try {
			onSelect(normalizeSelectionIds(newSelection));
		} catch {
			onSelect([]);
		}
	};

	const handleColumnHeaderClick = (params, event) => {
		if (!pageScopedHeaderSelection || !checkboxSelection || !onSelect) return;
		if (params?.field !== "__check__") return;
		if (event) event.defaultMuiPrevented = true;

		const pageIds = getCurrentPageIds();
		const pageSet = new Set(pageIds);
		const prevIds = normalizeSelectionIds(rowSelectionModel);
		const prevSet = new Set(prevIds);
		const allPageSelected = pageIds.length > 0 && pageIds.every((id) => prevSet.has(id));

		const nextIds = allPageSelected
			? prevIds.filter((id) => !pageSet.has(id))
			: Array.from(new Set([...prevIds, ...pageIds]));

		suppressNextSelectionEventRef.current = true;
		onSelect(nextIds);
	};

	const handleSearchChange = (e) => {
		const query = e.target.value.toLowerCase();
		setSearchText(query);
		// filtering happens via useEffect/runSearch
	};

	// FIXED: Safe row ID getter with comprehensive fallbacks
	const getRowIdSafe = useCallback((row) => {
		if (!row) {
			console.warn('DATAGRID: Attempted to get ID from null/undefined row');
			return `fallback-${Math.random()}`;
		}

		// Try configured field first
		if (rowIdField && row[rowIdField] !== null && row[rowIdField] !== undefined) {
			return row[rowIdField];
		}

		// Try common ID fields
		const possibleIds = [
			row.id,
			row.document_id,
			row.uuid,
			row._id,
			row.key
		].filter(id => id !== null && id !== undefined && id !== '');

		if (possibleIds.length > 0) {
			return possibleIds[0];
		}

		// Last resort: generate unique ID
		console.warn('DATAGRID: No valid ID found for row, generating fallback:', row);
		return `fallback-${JSON.stringify(row).substring(0, 50)}-${Math.random()}`;
	}, [rowIdField]);

	const escapeDataGridRowId = (value) => {
		const stringValue = String(value);
		if (typeof window !== "undefined" && window.CSS?.escape) {
			return window.CSS.escape(stringValue);
		}
		return stringValue.replace(/["\\]/g, "\\$&");
	};
	// Determine if we're using server-side pagination
	const isServerMode = paginationMode === "server";

	useEffect(() => {
		if (isServerMode) return;
		setClientPaginationModel((prev) => {
			if (Number(prev?.pageSize) === normalizedPageSize) return prev;
			return { page: 0, pageSize: normalizedPageSize };
		});
	}, [isServerMode, normalizedPageSize]);

	const handlePaginationModelChangeInternal = (model) => {
		if (!isServerMode) setClientPaginationModel(model);
		if (typeof onPaginationModelChange === "function") onPaginationModelChange(model);
	};

	useEffect(() => {
		if (!scrollToRowId || isServerMode) return;
		const targetId = String(scrollToRowId);
		const rowIndex = safeRows.findIndex((row) => String(getRowIdSafe(row)) === targetId);
		if (rowIndex < 0) return;
		const pageSizeForScroll = Number(clientPaginationModel?.pageSize) || normalizedPageSize;
		const nextPage = Math.floor(rowIndex / pageSizeForScroll);
		setClientPaginationModel((prev) =>
			Number(prev?.page) === nextPage ? prev : { ...prev, page: nextPage }
		);

		let firstFrame = 0;
		let secondFrame = 0;
		firstFrame = window.requestAnimationFrame(() => {
			secondFrame = window.requestAnimationFrame(() => {
				const escapedId = escapeDataGridRowId(targetId);
				const rowElement = gridWrapperRef.current?.querySelector(`[data-id="${escapedId}"]`);
				rowElement?.scrollIntoView({ behavior: "smooth", block: "center" });
			});
		});
		return () => {
			window.cancelAnimationFrame(firstFrame);
			window.cancelAnimationFrame(secondFrame);
		};
	}, [
		clientPaginationModel?.pageSize,
		getRowIdSafe,
		isServerMode,
		normalizedPageSize,
		safeRows,
		scrollToRowId,
	]);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "fit-content",
				width: "100%",
				minWidth: 0,
				marginBottom: "30px",
			}}
		>
			{/* Show toolbar if search bar OR custom buttons exist */}
			{(showSearchBar || (customButtons && customButtons.length > 0)) && (
				<div
					style={{
						display: "flex",
						justifyContent: showSearchBar ? "space-between" : "flex-end",
						alignItems: "center",
						marginBottom: "0px",
					}}
				>
					{/* Only show search if enabled */}
					{showSearchBar && (
						<TextField
							variant="outlined"
							size="small"
							value={searchText}
							onChange={handleSearchChange}
							placeholder={t("PLACEHOLDER.SEARCH")}
							style={{ marginRight: "20px", minWidth: "20%", marginTop: customButtons?.length > 0 ? 0 : "15px" }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
					)}

					{/* Always show custom buttons if they exist */}
					{customButtons && customButtons.length > 0 && (
						<Toolbar
							style={{ display: "flex", gap: "8px", margin: 0, padding: 0 }}
							data-tour="admin-user-actions"
						>
							{customButtons.map((button, index) => (
								<Button
									key={index}
									startIcon={button?.icon}
									variant="contained"
									color={button?.color ? button?.color : "primary"}
									onClick={button.onClick}
									disabled={Boolean(button?.disabled)}
								>
									{button.label}
									{button?.upload && (
										<input
											type="file"
											hidden
											onChange={button?.handleFileChange}
											accept={button?.acceptedFile}
										/>
									)}
								</Button>
							))}
						</Toolbar>
					)}
				</div>
			)}

			<div
				ref={gridWrapperRef}
				style={{
					flexGrow: 1,
					width: "100%",
					maxWidth: "100%",
					minWidth: 0,
					overflowX: "auto",
					height: autoHeight
						? "inherit"
						: typeof gridHeight === "number"
							? `${gridHeight}px`
							: gridHeight || "420px",
				}}
			>
				<DataGrid
					localeText={localeText}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
					rows={safeRows}
					columns={enhancedColumns}
					pagination
					pageSizeOptions={normalizedPageSizeOptions}
					initialState={{
						pagination: {
							paginationModel: { pageSize: normalizedPageSize, page: 0 },
						},
						sorting: { sortModel: sortBy },
					}}
					{...(rowClick && { onRowClick })}
					rowSelectionModel={rowSelectionModel}
					keepNonExistentRowsSelected={keepNonExistentSelected}
					onRowSelectionModelChange={handleSelectionChange}
					onColumnHeaderClick={handleColumnHeaderClick}
					disableMultipleRowSelection={false}
					onPageChange={onPageChange}
					paginationModel={isServerMode ? paginationModel : clientPaginationModel}
					onPaginationModelChange={handlePaginationModelChangeInternal}
					autoHeight={autoHeight}
					disableRowSelectionOnClick
					checkboxSelection={checkboxSelection}
					getRowId={getRowIdSafe}
					getRowHeight={getRowHeight}
					density={density}
					loading={loading}
					slots={slots}
					{...(isServerMode && {
						paginationMode: "server",
						rowCount: rowCount || 0,
					})}
					{...(filterMode === "server" && {
						filterMode: "server",
						filterModel,
						onFilterModelChange,
					})}
					slotProps={{
						...slotProps,
						pagination: {
							...slotProps?.pagination,
							SelectProps: {
								...slotProps?.pagination?.SelectProps,
								MenuProps: {
									disablePortal: false,
									style: { zIndex: 1500 },
								},
							},
						},
					}}
					sx={{
						width: "100%",
						minWidth: 0,
						borderRadius: "10px",
						backgroundColor: (theme) => theme.palette.background.paper,
						"& .MuiDataGrid-main": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-virtualScroller": { overflowX: "auto !important" },
						"& .MuiDataGrid-cell": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-columnHeaders": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-columnHeader": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-footerContainer": { backgroundColor: (theme) => theme.palette.background.paper },
						".MuiTablePagination-selectLabel": { marginBottom: 0 },
						".MuiTablePagination-displayedRows": { marginBottom: 0 },
						...(disableSelectAll && {
							"& .MuiDataGrid-columnHeaderCheckbox .MuiButtonBase-root": { display: "none" },
							"& .MuiDataGrid-columnHeaderCheckbox": { pointerEvents: "none" },
						}),
						mt: customButtons?.length > 0 ? 0 : 2,
						...(gridSx || {}),
					}}
				/>
			</div>
		</div>
	);
};

export default Datagrid;
